import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, ShieldAlert, Plane, HeartCrack, Check, X, Clock, User } from "lucide-react";
import { TIPOS_SOLICITUD, FUERZA_MAYOR } from "@/lib/constants";
import { formatFechaLarga } from "@/lib/agenda";
import { nombreTourDisplay } from "@/lib/tourDisplay";

function FuerzaMayorIcon({ tipo }) {
  if (tipo === "vuelo_cancelado") return <Plane className="h-4 w-4" />;
  if (tipo === "fallecimiento") return <HeartCrack className="h-4 w-4" />;
  return <ShieldAlert className="h-4 w-4" />;
}

export default function AprobacionesTab({ onCount, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [motivos, setMotivos] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cancelaciones/aprobaciones", { timeout: 20000 });
      setItems(data);
      onCount?.(data.length);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [onCount]);

  useEffect(() => {
    load();
  }, [load]);

  const decidir = async (id, decision, tipo) => {
    setBusyId(id);
    try {
      await api.post(`/cancelaciones/solicitudes/${id}/decidir`, {
        decision,
        motivo: motivos[id] || "",
      });
      if (decision === "aprobar") {
        toast.success(tipo === "cambio_pax" ? "Cambio de pasajeros aplicado." : "Aprobada y aplicada. Se generó la respuesta al cliente.");
      } else {
        toast.success("Solicitud rechazada.");
      }
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="p-10 text-center text-slate-400 text-sm" data-testid="aprobaciones-empty">
        <ShieldAlert className="h-8 w-8 mx-auto mb-2 text-slate-300" />
        No hay solicitudes pendientes de aprobación.
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-slate-500">
        Solicitudes fuera de plazo (fuerza mayor, o cambios/cancelaciones/pasajeros de agencias con menos
        antelación de la política). Revisá la información y decidí. Tu decisión queda registrada en la auditoría.
      </p>
      {items.map((a) => (
        <Card key={a.id} className="p-4" data-testid={`aprobacion-${a.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            {a.fuerza_mayor ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 text-[11px] font-semibold">
                <FuerzaMayorIcon tipo={a.fuerza_mayor} />
                {FUERZA_MAYOR[a.fuerza_mayor] || "Fuerza mayor"}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200 px-2.5 py-1 text-[11px] font-semibold">
                <Clock className="h-3.5 w-3.5" /> {a.origen === "agencia" ? "Solicitud de agencia" : "Fuera de plazo"}
              </span>
            )}
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border bg-slate-100 text-slate-700 border-slate-200">
              {TIPOS_SOLICITUD[a.tipo] || a.tipo}
            </span>
            <span className="font-mono text-[11px] text-slate-500">{a.codigo_booking}</span>
            <span className="text-[11px] text-slate-400">{a.pax} pax</span>
          </div>

          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
            <p className="flex items-center gap-1.5 text-slate-700">
              <User className="h-3.5 w-3.5 text-slate-400" /> {a.cliente_nombre || "—"}
            </p>
            <p className="flex items-center gap-1.5 text-slate-700">
              <Clock className="h-3.5 w-3.5 text-slate-400" /> {nombreTourDisplay(a.tour_nombre, a.modalidad)} · {formatFechaLarga(a.fecha_salida)} {a.hora_salida}
            </p>
            <p className="text-slate-500">
              Llegó a {a.horas_restantes != null ? `${a.horas_restantes}h` : "—"} de la salida · política {a.horas_politica}h
            </p>
            {a.tipo === "reprogramacion" && a.fecha_hora_nueva && (
              <p className="text-indigo-700 font-medium">
                Nueva fecha solicitada: {a.fecha_hora_nueva.fecha} · {a.fecha_hora_nueva.hora}
              </p>
            )}
            {a.tipo === "cambio_pax" && a.pax_nuevo && (
              <p className="text-indigo-700 font-medium">
                Pasajeros solicitados: {a.pax_nuevo.adultos} ad · {a.pax_nuevo.ninos} niñ · {a.pax_nuevo.infantes} inf
              </p>
            )}
          </div>

          {a.texto && (
            <p className="mt-2 text-[12px] text-slate-600 bg-slate-50 border border-slate-100 rounded px-3 py-2 line-clamp-3">
              “{a.texto}”
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-2 mt-3 pt-3 border-t border-slate-100">
            <Input
              placeholder="Motivo de la decisión (opcional)"
              value={motivos[a.id] || ""}
              onChange={(e) => setMotivos((m) => ({ ...m, [a.id]: e.target.value }))}
              className="flex-1"
              data-testid={`aprobacion-motivo-${a.id}`}
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => decidir(a.id, "rechazar", a.tipo)}
                disabled={busyId === a.id}
                data-testid={`aprobacion-rechazar-${a.id}`}
                className="gap-1.5 text-red-600 hover:text-red-700"
              >
                <X className="h-4 w-4" /> Rechazar
              </Button>
              <Button
                onClick={() => decidir(a.id, "aprobar", a.tipo)}
                disabled={busyId === a.id}
                data-testid={`aprobacion-aprobar-${a.id}`}
                className="gap-1.5"
              >
                {busyId === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Aprobar
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
